import { Menu, Text } from '@mantine/core';
import { modals } from '@mantine/modals';
import { SendingMediaNames } from 'api/models';
import classNames from 'classnames/bind';
import dayjs from 'dayjs';
import { useCompanyConfiguration } from 'init/use-company-configuration';
import { i18nKeys, useTranslation } from 'locales';
import { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { LegalCityModal } from 'shared/components/InvoiceActionsDropdown/LegalCityModal/LegalCityModal';
import { ThirdPartyType, useLoadThirdParties } from 'shared/hooks/use-load-third-parties';
import { treatInvoice } from 'shared/serializer';
import { invoiceActions } from 'store/invoice/invoice.actions';
import { DialogShowId, DialogShowSize, dialogHide, showDialog } from 'store/view/view.actions';
import type { Id } from 'types';
import type { GroupedInvoiceProps } from 'types/invoiceActions';
import type { Invoice, InvoiceActions as InvoiceActionsType } from 'types/store/invoice-state';
import type { StoreState } from 'types/storeTypes';
import AYSModal from '../AYSModal';
import { hasSendingMedium } from '../SendingMedium';
import AdditionalReminder from './AdditionalReminder';
import { AmlModal } from './AmlModal';
import ChooseInvoicesForm from './ChooseInvoicesForm';
import { ChooseThirdPartyTypeForm } from './ChooseThirdPartyTypeForm';
import ConfirmBailiffForm from './ConfirmBailiffForm';
import FormalNotice from './FormalNotice';
import { GcollectModal } from './GcollectModal';
import styleIdentifiers from './invoiceActions.module.scss';
import SelectThirdPartyForm from './SelectThirdPartyForm';

const styles = classNames.bind(styleIdentifiers);

type InvoiceActionsDropdownProps = {
  invoice: Invoice;
  item: React.ReactElement;
  reload: Function;
  selectorClass?: string;
  // This component is used on each row of the index of invoices to handle and we don't want to fire all
  // those requests off at once, so we optionally defer it until the user clicks on the dropdown
  shouldDeferRequest?: boolean;
};

export const InvoiceActionsDropdown = ({
  reload,
  invoice,
  item,
  selectorClass,
  shouldDeferRequest: _shouldDeferRequest = false,
}: InvoiceActionsDropdownProps) => {
  const { t } = useTranslation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [shouldDeferRequest, setShouldDeferRequest] = useState(_shouldDeferRequest);
  const enableRequest = () => setShouldDeferRequest(false);
  const company = useCompanyConfiguration();
  const constants = useSelector((state: StoreState) => state.app.constants);

  const { thirdParties } = useLoadThirdParties({
    id: invoice.id,
    enabled: !shouldDeferRequest,
  });

  const additionalReminderAction = useCallback(
    (fullInvoice: Invoice) => {
      showDialog({
        id: DialogShowId.CONFIRM,
        size: DialogShowSize.SMALL,
        title: t(i18nKeys.LATE_FEES),
        children: (
          <AYSModal
            text={t(i18nKeys.ADD_LATE_FEES)}
            cancelButtonText={t(i18nKeys.NO)}
            confirmButtonText={t(i18nKeys.YES)}
            onCancel={() => {
              additionalReminderOptions({ add_late_fees: false, fullInvoice });
            }}
            onConfirm={() => {
              additionalReminderOptions({ add_late_fees: true, fullInvoice });
            }}
          />
        ),
      });
    },
    [invoice],
  );

  // Sentry 18Z: Apparently CustomTable can pass a nullish invoice since it is typed as any
  if (!invoice) return null;

  const callbackAction = () => {
    reload?.();
  };

  const isInactive = (type: InvoiceActionsType) => !invoice.actions.includes(type);

  const confirmThirdParty = (additionalInvoices: any) => (value: any) => {
    dialogHide(DialogShowId.CUSTOM);
    invoiceActions.actionsThirdParty({
      id: invoice.id,
      callback: callbackAction,
      data: {
        third_party_company_id: value.third_party_company_id,
        additional_invoices: additionalInvoices,
      },
    });
  };

  const canSendByPost = !(
    !constants.external_services.postgreen.active &&
    hasSendingMedium(company.sending_media_attributes, SendingMediaNames.post)
  );

  const selectGroupedInvoices = ({ type, onSubmit, text, data = {} }: GroupedInvoiceProps) => {
    showDialog({
      id: DialogShowId.CUSTOM,
      size: DialogShowSize.LARGE,
      title: t(text || i18nKeys.FORM.CHOOSE_INVOICES.TITLE),
      children: (
        <ChooseInvoicesForm
          getData={(callback: any) => {
            invoiceActions.getGroupedInvoices({
              id: invoice.id,
              data: {
                debtor_id: invoice.debtor_id,
                status: 'late_unpaid',
                group_type: type,
                ...data,
              },
              callback,
            });
          }}
          type={type}
          onSubmit={submitData => {
            onSubmit(submitData);
          }}
        />
      ),
    });
  };

  const additionalReminderOptions = ({ fullInvoice, ...lateFeesOptions }: any) => {
    selectGroupedInvoices({
      type: 'additional_reminder',
      data: lateFeesOptions,
      onSubmit: ({ invoiceActionData, ...items }) =>
        showDialog({
          id: DialogShowId.CUSTOM,
          size: DialogShowSize.LARGE,
          title: t(i18nKeys.FORM.ADDITIONAL_REMINDER.CONFIRM_SENDING_OPTIONS),
          children: (
            <AdditionalReminder
              items={{ ...items, ...lateFeesOptions }}
              onSubmit={(values: any) => {
                invoiceActions.actionsAdditionalReminder({
                  id: invoice.id,
                  data: {
                    ...values,
                    ...items,
                    ...lateFeesOptions,
                    template_id: values.template_id?.id || values.template_id,
                  },
                  callback: () => {
                    dialogHide(DialogShowId.CUSTOM);
                    callbackAction();
                  },
                });
              }}
              invoiceActionsData={invoiceActionData}
              initialValues={fullInvoice.sending_options_attributes}
              invoice={{
                ...fullInvoice,
                debtor_attributes: {
                  ...fullInvoice.debtor_attributes,
                  emails: [
                    ...fullInvoice.debtor_attributes.emails,
                    ...fullInvoice.sending_options_attributes.emails,
                  ],
                },
              }}
            />
          ),
        }),
    });
  };

  const additionalReminder = () => {
    if (isInactive('additional_reminder') || !canSendByPost) return;

    if (invoice.debtor_attributes) {
      additionalReminderAction(invoice);
    } else {
      invoiceActions.detail({
        id: invoice.id,
        callback: ({ data }: any) => {
          additionalReminderAction(treatInvoice(data));
        },
      });
    }
  };

  const _editClient = (debtor: any) => {
    _editClient({
      client: debtor,
      callback: callbackAction,
    });
  };

  const sendFormalNotice = (items: any) => (data: any) => {
    invoiceActions.actionsFormalNotice({
      data: {
        ...data,
        ...items,
      },
      id: invoice.id,
      callback: () => {
        dialogHide(DialogShowId.CUSTOM);
        callbackAction();
      },
    });
  };

  const openFormalNoticeModal = (selectedInvoice: Invoice) => {
    selectGroupedInvoices({
      type: 'formal_notice',
      onSubmit: ({ invoiceActionData, ...items }) =>
        showDialog({
          id: DialogShowId.CUSTOM,
          size: DialogShowSize.LARGE,
          keepMountOnExit: true,
          title: t(i18nKeys.FORM.FORMAL_NOTICE.CONFIRM),
          children: (
            <FormalNotice
              invoiceActionData={invoiceActionData}
              invoice={selectedInvoice}
              items={items}
              onSubmit={sendFormalNotice(items)}
            />
          ),
        }),
    });
  };

  const formalNotice = () => {
    if (isInactive('formal_notice') || !canSendByPost) return;

    if (invoice.type === 'actionable_invoice') {
      invoiceActions.detail({
        id: invoice.id,
        callback: ({ data }: any) => {
          openFormalNoticeModal(treatInvoice(data));
        },
      });
    } else {
      openFormalNoticeModal(invoice);
    }
  };

  const markAsToBeProcessed = () => {
    invoiceActions.actionsMarkAsToBeProccesed({
      id: invoice.id,
      callback: callbackAction,
    });
  };

  const openGcollectFlow = (
    data: { third_party_company_id: number },
    additionalInvoiceIds: Array<string>,
  ) => {
    dialogHide(DialogShowId.CUSTOM);
    modals.open({
      title: t(i18nKeys.FOLLOW_UP.INVOICE_TO_PROCESS.GCOLLECT.CONFIRM_RATES),
      centered: true,
      size: 'xl',
      children: (
        <GcollectModal
          gcollectId={data.third_party_company_id}
          invoiceIds={[invoice.id]}
          additionalInvoiceIds={additionalInvoiceIds.map(Number)}
        />
      ),
    });
  };

  // AML = Anti-Money Laundering
  const openAMLModal = (
    data: { third_party_company_id: number },
    additionalInvoiceIds: Array<string>,
  ) => {
    dialogHide(DialogShowId.CUSTOM);
    modals.open({
      title: t(i18nKeys.AML_MODAL.TITLE),
      centered: true,
      size: '70%',
      children: (
        <AmlModal
          invoiceIds={[invoice.id]}
          thirdPartyCompanyId={data.third_party_company_id}
          additionalInvoiceIds={additionalInvoiceIds.map(Number)}
          onSubmit={() => {}}
        />
      ),
    });
  };

  const openLegalCityModal = (
    data: { third_party_company_id: number },
    additionalInvoiceIds: Array<string>,
  ) => {
    dialogHide(DialogShowId.CUSTOM);
    modals.open({
      title: 'LegalCity',
      centered: true,
      children: (
        <LegalCityModal
          legalcityId={String(data.third_party_company_id)}
          invoiceIds={[invoice.id]}
          additionalInvoiceIds={additionalInvoiceIds.map(Number)}
        />
      ),
    });
  };

  const lawyer = () => {
    selectGroupedInvoices({
      type: ThirdPartyType.LAWYER,
      onSubmit: ({ additional_invoices }) =>
        showDialog({
          id: DialogShowId.CUSTOM,
          size: DialogShowSize.SMALL,
          keepMountOnExit: true,
          title: t(i18nKeys.FORM.LAWYER_TITLE),
          children: (
            <SelectThirdPartyForm
              selectedThirdPartyType={ThirdPartyType.LAWYER}
              thirdParties={thirdParties!}
              onSubmit={confirmThirdParty(additional_invoices)}
              onSubmitAML={(data: any) => openAMLModal(data, additional_invoices)}
            />
          ),
        }),
    });
  };

  const debtCollector = () => {
    selectGroupedInvoices({
      type: ThirdPartyType.DEBT_COLLECTOR,
      onSubmit: ({ additional_invoices = [] }) =>
        showDialog({
          id: DialogShowId.CUSTOM,
          size: DialogShowSize.SMALL,
          keepMountOnExit: true,
          title: t(i18nKeys.FORM.DEBT_COLLECTOR_TITLE),
          children: (
            <SelectThirdPartyForm
              selectedThirdPartyType={ThirdPartyType.DEBT_COLLECTOR}
              thirdParties={thirdParties!}
              onSubmit={confirmThirdParty(additional_invoices)}
              onSubmitGcollect={(data: any) => openGcollectFlow(data, additional_invoices)}
              onSubmitLegalCity={(data: any) => openLegalCityModal(data, additional_invoices)}
            />
          ),
        }),
    });
  };

  const sendBailiff = (values: any) => {
    dialogHide(DialogShowId.CUSTOM);
    values.contested = !values.notContested;

    invoiceActions.actionsThirdParty({
      id: invoice.id,
      data: values,
      callback: callbackAction,
    });
  };

  const bailiff = () => {
    selectGroupedInvoices({
      type: 'bailiff',
      text: i18nKeys.FORM.CHOOSE_INVOICES.BAILIFF_TITLE,
      onSubmit: ({ additional_invoices }) =>
        showDialog({
          id: DialogShowId.CUSTOM,
          size: DialogShowSize.MEDIUM,
          keepMountOnExit: true,
          title: t(i18nKeys.FORM.CONFIRM_BAILIFF.BAILIFF_CHOICE),
          children: (
            <SelectThirdPartyForm
              thirdParties={thirdParties!}
              selectedThirdPartyType={ThirdPartyType.BAILIFF}
              onSubmit={(chosenBailiff: { third_party_company_id: Id }) => {
                showDialog({
                  id: DialogShowId.CUSTOM,
                  size: DialogShowSize.MEDIUM,
                  keepMountOnExit: true,
                  title: t(i18nKeys.FORM.CONFIRM_BAILIFF.IS_INVOICE_CONTESTABLE),
                  children: (
                    <ConfirmBailiffForm
                      bailiffId={chosenBailiff.third_party_company_id}
                      onSubmit={(values: any) =>
                        sendBailiff({
                          ...chosenBailiff,
                          ...values,
                          additional_invoices,
                        })
                      }
                    />
                  ),
                });
              }}
            />
          ),
        }),
    });
  };

  const selectThirdPartyType = () => {
    showDialog({
      id: DialogShowId.CUSTOM,
      size: DialogShowSize.LARGE,
      title: t(i18nKeys.FORM.CHOOSE_THIRD_PARTY_TYPE.TITLE),
      children: (
        <ChooseThirdPartyTypeForm
          inheritedThirdParties={thirdParties}
          invoices={[invoice]}
          onSubmit={selectedThirdPartType => {
            switch (selectedThirdPartType) {
              case ThirdPartyType.LAWYER:
                lawyer();
                break;
              case ThirdPartyType.BAILIFF:
                bailiff();
                break;
              case ThirdPartyType.DEBT_COLLECTOR:
                debtCollector();
                break;
              default:
                break;
            }
          }}
        />
      ),
    });
  };

  return (
    <div onClick={e => e.stopPropagation()}>
      <Menu
        opened={isMenuOpen}
        onChange={setIsMenuOpen}
        onOpen={enableRequest}
        position="bottom-end"
        width={300}
        withinPortal
        classNames={{ dropdown: styles('content-class') }}
      >
        <Menu.Target>
          <div className={selectorClass}>{item}</div>
        </Menu.Target>
        <Menu.Dropdown onClick={() => setIsMenuOpen(false)}>
          <Menu.Item
            disabled={isInactive('additional_reminder') || !canSendByPost}
            onClick={additionalReminder}
          >
            <Text size="sm">{t(i18nKeys.FOLLOW_UP.INVOICE_TO_PROCESS.SEND_REMINDER)}</Text>
            {!canSendByPost && (
              <Text size="xs" c="red">
                {t(i18nKeys.SERVICE_UNAVAILABLE_UNTIL, {
                  date: dayjs(constants.external_services.postgreen.unavailable_until!).format('L'),
                })}
              </Text>
            )}
          </Menu.Item>

          <Menu.Item
            disabled={isInactive('formal_notice') || !canSendByPost}
            onClick={formalNotice}
          >
            <Text size="sm">{t(i18nKeys.FOLLOW_UP.INVOICE_TO_PROCESS.SEND_FORMAL_NOTICE)}</Text>
            <Text size="xs" c="dimmed">
              {t(i18nKeys.FOLLOW_UP.INVOICE_TO_PROCESS.FORMAL_NOTICE_SEND_BY_MAIL)}
            </Text>
            {!canSendByPost && (
              <Text size="xs" c="red">
                {t(i18nKeys.SERVICE_UNAVAILABLE_UNTIL, {
                  date: dayjs(constants.external_services.postgreen.unavailable_until!).format('L'),
                })}
              </Text>
            )}
          </Menu.Item>

          <Menu.Item
            disabled={!thirdParties?.areAnyThirdPartiesAccessible}
            onClick={selectThirdPartyType}
          >
            <Text size="sm">{t(i18nKeys.FOLLOW_UP.INVOICE_TO_PROCESS.SEND_TO_THIRD_PARTY)}</Text>
            <Text size="xs" c="dimmed">
              {t(i18nKeys.FOLLOW_UP.INVOICE_TO_PROCESS.THIRD_PARTIES)}
            </Text>
          </Menu.Item>

          <Menu.Item disabled={isInactive('mark_as_to_be_processed')} onClick={markAsToBeProcessed}>
            <Text size="sm">{t(i18nKeys.FOLLOW_UP.INVOICE_TO_PROCESS.TAKE_OUT_OF_PROCESS)}</Text>
            <Text size="xs" c="dimmed">
              {t(i18nKeys.FOLLOW_UP.INVOICE_TO_PROCESS.NO_MORE_AUTOMATIC_ACTIONS)}
            </Text>
          </Menu.Item>
        </Menu.Dropdown>
      </Menu>
    </div>
  );
};
